import React, { useEffect, useRef, useState } from 'react';
import {
  FormControl,
  Grid,
  IconButton,
  Input,
  ListItemText,
  ListSubheader,
  makeStyles,
  Menu,
  MenuItem,
  Radio,
  Select,
  Typography,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CloseIcon from '@material-ui/icons/Close';
import CustomUploadField from '../shared/CustomUploadField';
import { DIMENSIONS } from '../../constants/aspectRatio';
import { useExercise } from './hooks/useExercise';
import {
  AI_EXERCISE_CUSTOM_ROLE,
  AI_EXERCISE_ROLE_DEPARTMENTS,
  AI_EXERCISE_ROLE_OPTIONS,
  EXERCISE_LEVEL,
} from './contants';
import { RockedAudio } from '../common/RockedAudio';
import CustomSwitch from '../shared/CustomSwitch';

const useStyle = makeStyles({
  content: {
    gap: '20px',
    paddingBottom: '10px',
  },
  required_field: {
    color: '#667085',
    '&:after': {
      content: "' *'",
      color: '#F50000',
    },
  },
  option_field: {
    color: '#667085',
  },
  inputField: {
    width: '96%',
    padding: '15px 10px',
    borderRadius: '5px',
    border: '1px solid #E4E7EC',
    outline: 'none',
    marginTop: '5px',
    background: 'white',
    fontSize: '14px',
  },
  textArea: {
    width: '98%',
    height: '70px',
    padding: '15px 10px',
    borderRadius: '5px',
    border: '1px solid #E4E7EC',
    outline: 'none',
    marginTop: '5px',
    background: 'white',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'sans-serif',
  },
  upload: {
    border: '1px solid #E4E7EC',
    padding: '7px 10px',
    marginTop: '5px',
    width: '100%',
    borderRadius: '5px',
    background: 'white',
  },
  item: {
    width: '45%',
  },
  smalltext: {
    font: '14px',
    color: '#667085',
    marginRight: '5px',
  },
  bold: {
    color: '#10182B',
    gontSize: '16px',
    marginBottom: '15px',
  },
  cta: {
    color: '#622FEE',
    fontSize: '14px',
    cursor: 'pointer',
  },
  popInputs: {
    border: 0,
    outline: 0,
    backgroundColor: 'white',
    padding: '13px 10px',
    '&:placeholder': {
      color: '#4b4b4b',
      opacity: '1',
    },
  },
  roleFieldWrapper: {
    position: 'relative',
    width: '96%',
    marginTop: '5px',
  },
  inputFieldWithIcon: {
    boxSizing: 'border-box',
    width: '100%',
    padding: '15px 34px 15px 10px',
    borderRadius: '5px',
    border: '1px solid #E4E7EC',
    outline: 'none',
    background: 'white',
    fontSize: '14px',
  },
  roleDropdownBtn: {
    position: 'absolute',
    right: '2px',
    top: '50%',
    transform: 'translateY(-50%)',
    padding: '4px',
  },
  roleGroupHeader: {
    backgroundColor: '#fff',
    color: '#667085',
    lineHeight: '32px',
    fontWeight: 600,
  },
});

export const ExerciseInfo = ({
  exerciseInfo,
  setExerciseInfo,
  handleChange,
  thumbnail,
  thumbnailImg,
  imageData,
  handleImageUpload,
  handleDeleteThumbnail,
  handleAddOverview,
  overView,
  handleChangeOverview,
  handleRemove,
}) => {
  const classes = useStyle();
  const [audioUrl, setAudioUrl] = useState('');
  const userRoleInputRef = useRef(null);
  const [userRoleMenuAnchor, setUserRoleMenuAnchor] = useState(null);
  const [isRoleSelectedFromList, setIsRoleSelectedFromList] = useState(
    () => !!exerciseInfo.userRole && AI_EXERCISE_ROLE_OPTIONS.some((option) => option.role === exerciseInfo.userRole),
  );

  const { getTypes, types, getAICharacters, AICharacters } = useExercise();

  useEffect(() => {
    getTypes();
    getAICharacters();
  }, []);

  // exerciseInfo.id only becomes truthy once the edit-mode fetch (getExerciseInfo in
  // ExercisePopup) resolves, so this fires exactly once per loaded record — not on every
  // keystroke — re-deriving the lock state against the role that actually came back.
  useEffect(() => {
    if (exerciseInfo.id) {
      setIsRoleSelectedFromList(AI_EXERCISE_ROLE_OPTIONS.some((option) => option.role === exerciseInfo.userRole));
    }
    // Intentionally keyed only on id: re-including userRole would re-run this on every
    // keystroke and reintroduce the mid-typing lockout this effect exists to avoid.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exerciseInfo.id]);

  useEffect(() => {
    if (exerciseInfo.botCharacter && AICharacters) {
      const botChar = AICharacters?.find((item) => item.id === exerciseInfo.botCharacter)?.audioSample || '';
      setAudioUrl(botChar);
    }
  }, [AICharacters, exerciseInfo]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      },
    },
  };

  return (
    <Grid container style={{ height: '100%' }}>
      <Grid item xs={12}>
        <Grid container className={classes.content}>
          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Exercise Name </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      placeholder="Enter exercise name"
                      className={classes.inputField}
                      value={exerciseInfo.name}
                      onChange={(e) => handleChange(e, 'name')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Thumbnail (max size - 100kb) </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomUploadField
                      type="image"
                      name="thumbnail"
                      value={thumbnailImg}
                      imgSrc={!imageData ? thumbnail : null}
                      handleChange={handleImageUpload}
                      customStyles={{ width: '100%' }}
                      fileName={imageData?.name}
                      dimensions={DIMENSIONS.aiExercise}
                      handleDelete={handleDeleteThumbnail}
                      containerStyles={{ borderRadius: '4px' }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Exercise Type</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl style={{ marginTop: '5px' }}>
                      <Select
                        labelId="multiple-checkbox-label"
                        id="multiple-checkbox"
                        input={<Input disableUnderline className={classes.popInputs} />}
                        MenuProps={MenuProps}
                        value={exerciseInfo.type}
                        onChange={(e) => handleChange(e, 'type')}
                      >
                        {types?.map((option) => (
                          <MenuItem name={option.name} value={option.id}>
                            <ListItemText primary={option.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Level of Exercise </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container alignItems="center" spacing={2}>
                      {EXERCISE_LEVEL?.map((item) => (
                        <Grid item key={item.id}>
                          <Grid container alignItems="center">
                            <Radio
                              className={classes.radioBtn}
                              checked={item.name === exerciseInfo.level}
                              style={{ ...(item.name === exerciseInfo.level ? { color: '#622fee' } : {}) }}
                              onClick={() => setExerciseInfo((pre) => ({ ...pre, level: item.name }))}
                            />
                            <Typography className={classes.optional_field}>{item.name} </Typography>
                          </Grid>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Time Limit (in mins) </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      placeholder="Enter time limit here"
                      className={classes.inputField}
                      value={exerciseInfo.timeLimit}
                      onChange={(e) => handleChange(e, 'timeLimit')}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item className={classes.item}>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}> Save Transcript</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <CustomSwitch
                      name="showTranscript"
                      checked={exerciseInfo?.showTranscript}
                      handleChange={(e) => handleChange(e, 'showTranscript')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Role of User </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.roleFieldWrapper}>
                      <input
                        ref={userRoleInputRef}
                        placeholder="Enter role of user"
                        className={classes.inputFieldWithIcon}
                        style={
                          isRoleSelectedFromList
                            ? { backgroundColor: '#F9FAFB', cursor: 'pointer' }
                            : undefined
                        }
                        value={exerciseInfo.userRole}
                        readOnly={isRoleSelectedFromList}
                        onClick={(e) => {
                          if (isRoleSelectedFromList) {
                            setUserRoleMenuAnchor(e.currentTarget);
                          }
                        }}
                        onChange={isRoleSelectedFromList ? undefined : (e) => handleChange(e, 'userRole')}
                      />
                      <IconButton
                        size="small"
                        className={classes.roleDropdownBtn}
                        onClick={(e) => setUserRoleMenuAnchor(e.currentTarget)}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                      <Menu
                        anchorEl={userRoleMenuAnchor}
                        open={Boolean(userRoleMenuAnchor)}
                        onClose={() => setUserRoleMenuAnchor(null)}
                        TransitionProps={{ onExited: () => userRoleInputRef.current?.focus() }}
                        PaperProps={{ style: { maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP } }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleChange({ target: { value: '' } }, 'userRole');
                            setIsRoleSelectedFromList(false);
                            setUserRoleMenuAnchor(null);
                          }}
                        >
                          <ListItemText primary={AI_EXERCISE_CUSTOM_ROLE} />
                        </MenuItem>
                        {AI_EXERCISE_ROLE_DEPARTMENTS.flatMap((department) => [
                          <ListSubheader key={`role-dept-${department}`} className={classes.roleGroupHeader}>
                            {department}
                          </ListSubheader>,
                          ...AI_EXERCISE_ROLE_OPTIONS.filter((option) => option.department === department).map(
                            (option) => (
                              <MenuItem
                                key={`${option.department}-${option.role}`}
                                onClick={() => {
                                  handleChange({ target: { value: option.role } }, 'userRole');
                                  setIsRoleSelectedFromList(true);
                                  setUserRoleMenuAnchor(null);
                                }}
                              >
                                <ListItemText primary={option.role} />
                              </MenuItem>
                            ),
                          ),
                        ])}
                      </Menu>
                    </div>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Role of Bot </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <input
                      placeholder="Enter role of bot"
                      className={classes.inputField}
                      value={exerciseInfo.botRole}
                      onChange={(e) => handleChange(e, 'botRole')}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item className={classes.item}>
                <Grid container>
                  <Grid item xs={12}>
                    <Typography className={classes.required_field}>Bot Character </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl style={{ marginTop: '5px' }}>
                      <Select
                        labelId="multiple-checkbox-label"
                        id="multiple-checkbox"
                        input={<Input disableUnderline className={classes.popInputs} />}
                        MenuProps={MenuProps}
                        value={exerciseInfo.botCharacter}
                        onChange={(e) => handleChange(e, 'botCharacter')}
                      >
                        {AICharacters?.map((option) => (
                          <MenuItem name={option.name} value={option.id}>
                            <ListItemText primary={option.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              {audioUrl ? (
                <Grid item className={classes.item}>
                  <Grid container>
                    <RockedAudio src={audioUrl} />
                  </Grid>
                </Grid>
              ) : null}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item xs={12}>
                <Typography className={classes.bold}>Exercise Overview (3 max)</Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container style={{ gap: '7px' }}>
                  {overView?.map((over, i) => (
                    <Grid item xs={12}>
                      <Grid container>
                        <Grid item xs={12}>
                          <Grid container justifyContent="space-between">
                            <Typography className={classes.required_field}>Overview Screen {i + 1}</Typography>
                            <Typography className={classes.smalltext} style={{ marginRight: '30px' }}>
                              {over.text?.length}/320
                            </Typography>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item style={{ width: '96%' }}>
                              <textarea
                                placeholder="Write overview here"
                                className={classes.textArea}
                                style={{ height: '50px' }}
                                value={over.text}
                                onChange={(e) => handleChangeOverview(e, 320, i)}
                              />
                            </Grid>
                            <Grid item>
                              <CloseIcon onClick={() => handleRemove(i)} style={{ cursor: 'pointer', color: 'gray' }} />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
              {overView?.length < 3 ? (
                <Grid item xs={12}>
                  <Typography className={classes.cta} onClick={handleAddOverview}>
                    + Add Screen
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Grid container justifyContent="space-between">
                      <Typography className={classes.required_field}>
                        Scenario Introduction (Phone Ring Screen)
                      </Typography>
                      <Typography className={classes.smalltext}>{exerciseInfo.scenarioIntro?.length}/320</Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <textarea
                      placeholder="Write scenario introduction here"
                      className={classes.textArea}
                      value={exerciseInfo.scenarioIntro}
                      onChange={(e) => handleChange(e, 'scenarioIntro', 320)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Grid container justifyContent="space-between">
                      <Typography className={classes.required_field}> Call Transfer Text</Typography>
                      <Typography className={classes.smalltext}>{exerciseInfo.callTransfer?.length}/320</Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <textarea
                      placeholder="Write call transfer text here"
                      className={classes.textArea}
                      value={exerciseInfo.callTransfer}
                      onChange={(e) => handleChange(e, 'callTransfer', 320)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              <Grid item xs={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Grid container justifyContent="space-between">
                      <Typography className={classes.required_field}>Bot Introduction (During Call)</Typography>
                      <Typography className={classes.smalltext}>{exerciseInfo.botIntro?.length}/320</Typography>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <textarea
                      placeholder="Write bot introduction here"
                      className={classes.textArea}
                      value={exerciseInfo.botIntro}
                      onChange={(e) => handleChange(e, 'botIntro', 320)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
